const RESERVED = new Set([
  '',
  'launchpad',
  'presale',
  'how-it-works',
  'my-colony',
  'colony',
  'explore-colony',
  'colony-newsfeed',
  'submit',
  'terms',
  'disclaimer',
  'calculator',
  'graduated',
  'sold-out',
  'presale-vs-quick-launch',
])

export function tokenNameSlug(name: string): string {
  const cleaned = (name ?? '').replace(/[^a-zA-Z0-9]+/g, '')
  return cleaned || 'Token'
}

export function collectShareSlugs(items: Array<{ shareSlug?: string } | string | undefined>): Set<string> {
  const taken = new Set<string>()
  for (const item of items) {
    const slug = typeof item === 'string' ? item : item?.shareSlug
    const key = slug?.trim().toLowerCase()
    if (key)
      taken.add(key)
  }
  return taken
}

export function uniqueShareSlug(
  name: string,
  taken: Set<string>,
  existing?: string,
): string {
  const current = existing?.trim()
  if (current && !RESERVED.has(current.toLowerCase()) && !taken.has(current.toLowerCase()))
    return current

  const base = tokenNameSlug(name)
  let candidate = base
  let n = 0
  while (RESERVED.has(candidate.toLowerCase()) || taken.has(candidate.toLowerCase())) {
    n += 1
    candidate = `${base}${n}`
  }
  return candidate
}

export function tokenSharePath(shareSlug?: string, fallbackId?: string): string {
  const slug = shareSlug?.trim() || fallbackId?.trim()
  return slug ? `/${slug}` : '/'
}

const SHARE_ORIGIN = 'https://penguinpad.xyz'

export function tokenShareUrl(shareSlug?: string, fallbackId?: string): string {
  const path = tokenSharePath(shareSlug, fallbackId)
  return `${SHARE_ORIGIN}${path}`
}

const PENGUIN_PAD_HOST = 'penguinpad.xyz'
const PROJECT_SEGMENT_RE = /^[A-Za-z0-9._-]+$/

function parsePenguinPadUrl(value: string): URL | null {
  const raw = value.trim()
  if (!raw)
    return null
  try {
    const parsed = raw.startsWith('/') && !raw.startsWith('//')
      ? new URL(raw, SHARE_ORIGIN)
      : new URL(raw)
    if (parsed.protocol !== 'https:')
      return null
    if (parsed.username || parsed.password)
      return null
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '')
    if (host !== PENGUIN_PAD_HOST)
      return null
    return parsed
  }
  catch {
    return null
  }
}

/** In-app path for an official Penguin Pad project page, or null. */
export function penguinPadProjectPath(value: string): string | null {
  const parsed = parsePenguinPadUrl(value)
  if (!parsed)
    return null
  const pathname = parsed.pathname.replace(/\/+$/, '') || '/'
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 1) {
    const slug = segments[0]
    if (RESERVED.has(slug.toLowerCase()) || !PROJECT_SEGMENT_RE.test(slug))
      return null
    return `/${slug}`
  }
  if (segments.length === 2) {
    const kind = segments[0].toLowerCase()
    const id = segments[1]
    if ((kind !== 'launchpad' && kind !== 'presale') || !PROJECT_SEGMENT_RE.test(id))
      return null
    return `/${kind}/${id}`
  }
  return null
}

/** Canonical https://penguinpad.xyz project URL, or null. */
export function penguinPadProjectUrl(value: string): string | null {
  const path = penguinPadProjectPath(value)
  return path ? `${SHARE_ORIGIN}${path}` : null
}

function readStoredShareSlugs(key: string): string[] {
  if (typeof window === 'undefined')
    return []
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw)
      return []
    const parsed = JSON.parse(raw) as unknown
    const list = Array.isArray(parsed)
      ? parsed
      : (parsed && typeof parsed === 'object' && Array.isArray((parsed as { launches?: unknown }).launches)
          ? (parsed as { launches: unknown[] }).launches
          : (parsed && typeof parsed === 'object' && Array.isArray((parsed as { presales?: unknown }).presales)
              ? (parsed as { presales: unknown[] }).presales
              : []))
    return list
      .map((item) => {
        if (!item || typeof item !== 'object')
          return ''
        const slug = (item as { shareSlug?: unknown }).shareSlug
        return typeof slug === 'string' ? slug : ''
      })
      .filter(Boolean)
  }
  catch {
    return []
  }
}

export function persistedShareSlugs(exclude?: string): Set<string> {
  const taken = collectShareSlugs([
    ...readStoredShareSlugs('penguin-pad-sim-launches'),
    ...readStoredShareSlugs('penguin-pad-sim-presales-v2'),
  ])
  const skip = exclude?.trim().toLowerCase()
  if (skip)
    taken.delete(skip)
  return taken
}
